import {
  ApiError,
  createApiErrorFromResponse,
  readApiErrorDetail,
} from "@airange/shared/api";
import type { UserSession } from "@airange/shared/auth";
import {
  bootstrapBrowserAppSession,
  createOidcClientIdResolver,
  createSsoSessionCoordinator,
  logoutAndClearLocalSession,
} from "@airange/shared/oidc-session";
import { createOidcBrowserClient } from "@airange/shared/oidc";
import { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import { EDU_OIDC_APP_KEY, SSO_API_BASE_URL, SSO_OIDC_SCOPE } from "./env";
import { clearSession, getAccessToken, setAccessToken, setSession } from "./session";

export type OidcCallbackCompleteResult = {
  redirectPath: string;
  user: UserSession;
};

export { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

async function requestCurrentUser(fetchImpl: typeof fetch, accessToken: string | null) {
  const response = await fetchImpl(`${SSO_API_BASE_URL}/api/auth/me`, {
    method: "GET",
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
  });
  if (!response.ok) {
    throw createApiErrorFromResponse(response, await readApiErrorDetail(response));
  }
  return parseJson<UserSession>(response);
}

const clientIdResolver = createOidcClientIdResolver({
  appKey: EDU_OIDC_APP_KEY,
  ssoApiBaseUrl: SSO_API_BASE_URL,
});

const oidcClient = createOidcBrowserClient({
  authority: SSO_API_BASE_URL,
  callbackPath: OIDC_CALLBACK_PATH,
  defaultRedirectPath: "/dashboard",
  pendingRedirectPathKey: "airange.edu.admin.oidc.pending_redirect_path",
  resolveClientId: clientIdResolver.resolveClientId,
  scope: SSO_OIDC_SCOPE,
  stateStorePrefix: "airange.edu.admin.oidc.state.",
  userStorePrefix: "airange.edu.admin.oidc.user.",
});

const ssoSessionCoordinator = createSsoSessionCoordinator<UserSession>({
  allowCookieBootstrap: true,
  clearSession,
  getAccessToken,
  requestCurrentUser,
  resolveClientId: async (fetchImpl, forceRefresh) => {
    if (forceRefresh) {
      clientIdResolver.reset();
    }
    return await clientIdResolver.resolveClientId(fetchImpl, forceRefresh);
  },
  setAccessToken,
  setSession,
  ssoApiBaseUrl: SSO_API_BASE_URL,
});

export function consumeOidcRedirectPath(defaultPath = "/dashboard") {
  return oidcClient.consumePendingRedirectPath(defaultPath);
}

export async function startOidcLogin(redirectPath: string, prompt: "none" | "login" = "login") {
  await oidcClient.startAuthorization(redirectPath, { prompt });
}

export async function completeOidcCallback(_code: string, _state: string, fetchImpl: typeof fetch = fetch): Promise<OidcCallbackCompleteResult> {
  return await ssoSessionCoordinator.completeOidcCallback(oidcClient.completeCallback, fetchImpl);
}

export async function refreshAccessToken(fetchImpl: typeof fetch = fetch) {
  return await ssoSessionCoordinator.refreshAccessToken(fetchImpl);
}

export async function bootstrapEduAppSession(fetchImpl: typeof fetch = fetch) {
  const bootstrapResponse = await fetchImpl("/api/auth/bootstrap", {
    method: "POST",
    credentials: "include",
  });
  if (!bootstrapResponse.ok) {
    throw createApiErrorFromResponse(bootstrapResponse, await readApiErrorDetail(bootstrapResponse));
  }
  await parseJson<unknown>(bootstrapResponse);
  return true;
}

async function bootstrapEduAppSessionFromSsoUser(
  user: UserSession,
  fetchImpl: typeof fetch,
) {
  try {
    await bootstrapEduAppSession(fetchImpl);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      return user;
    }
    throw error;
  }
  return user;
}

export async function bootstrapSession(fetchImpl: typeof fetch = fetch) {
  const user = await ssoSessionCoordinator.bootstrapSession(fetchImpl);
  if (!user) {
    return null;
  }
  const result = await bootstrapBrowserAppSession<UserSession>(
    {
      bootstrapAppSession: () => bootstrapEduAppSessionFromSsoUser(user, fetchImpl),
      clearSession,
    },
    fetchImpl,
  );
  return result.currentUser;
}

export async function logout(fetchImpl: typeof fetch = fetch) {
  await logoutAndClearLocalSession(
    {
      ssoApiBaseUrl: SSO_API_BASE_URL,
      clearLocalSession: clearSession,
      getAccessToken,
    },
    fetchImpl,
  );
}
